import Button from '@components/shared/Button'
import InputField from '@components/shared/FormFields/InputField'
import SelectField from '@components/shared/FormFields/SelectField'
import TextArea from '@components/shared/FormFields/TextArea'
import FormMessage from '@components/shared/FormMessage'
import { FormMessageProps } from '@components/shared/FormMessage/types'
import Modal from '@components/shared/Modal'
import ModalV2 from '@components/shared/ModalV2'
import { Suggestion } from '@components/woz-check/Register/styles'
import { contactSubjectKeys } from '@config/contactSubjectKeys'
import { yupResolver } from '@hookform/resolvers/yup'
import { pushContactFormInfoDataLayer } from '@logic/data-layers/pushContactFormInfoDataLayer'
import { Validator } from '@utils/validation'
import emailRequired from '@utils/validation/emailRequired'
import phoneRequired from '@utils/validation/phoneRequired'
import required from '@utils/validation/required'
import emailMisspelled, { all as allDomains } from 'email-misspelled'
import React, { FC, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import AddressSearch from '../AddressSearch'
import { Column, ContactFormWrapper, LabelWrapper, Row } from './styles'
import { ContactFormProps, ContactInputProps } from './types'

const emailChecker = emailMisspelled({ domains: allDomains })

const ContactForm: FC<ContactFormProps> = ({ title, label, subject, onSubmitted }) => {
  const [formMessage, setFormMessage] = useState<FormMessageProps | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false)
  const [suggestion, setSuggestion] = useState<string | null>(null)
  const [showWarning, setShowWarning] = useState<boolean>(false)

  const [address, setAddress] = useState<string | null>(null)
  const [dossier, setDossier] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ContactInputProps>({
    resolver: yupResolver(
      Validator({
        'first-name': required(),
        surname: required(),
        email: emailRequired(),
        'phone-number': phoneRequired(),
        subject: required(),
        text: required(),
      }),
    ),
  })

  useEffect(() => {
    setValue('subject', subject || '')
  }, [subject])

  const onSubmit = (data: ContactInputProps) => {
    if (suggestion && !showWarning) return setShowWarning(true)

    setIsLoading(true)
    setFormMessage(null)
    setShowWarning(false)
    setSuggestion(null)

    if (dossier) data['case-ref'] = dossier

    fetch(`${process.env.GATSBY_API_URL}/api/v2/web/contact-form`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...data,
      }),
    })
      .then(response => {
        setIsLoading(false)

        if (response.status === 204) {
          pushContactFormInfoDataLayer(data.subject, data['case-ref'])
          setIsSuccessModalOpen(true)

          setAddress(null)
          setDossier(null)
          reset()
          return
        }

        setFormMessage({
          type: 'error',
          message: 'Er is helaas iets fout gegaan.',
        })
      })
      .catch(err => {
        setIsLoading(false)
        setFormMessage({
          type: 'error',
          message: 'Er is helaas iets fout gegaan.',
        })
      })
  }

  const userEmail = watch('email')

  useEffect(() => {
    if (userEmail) {
      const result = emailChecker(userEmail)[0]
      setSuggestion(result ? result.corrected : null)
    }
  }, [userEmail])

  return (
    <>
      <ModalV2
        open={showWarning}
        title="Controleer uw e-mailadres"
        topNodes={
          <>
            <p>Weet u zeker dat uw e-mailadres correct is?</p>
            <div style={{ marginBottom: 24 }}>
              <InputField type="text" value={userEmail} disabled />
            </div>
          </>
        }
        buttons={[
          {
            label: 'Nee, wijzig mijn e-mailadres',
            variant: 'secondary',
            small: true,
            onClick: () => setShowWarning(false),
          },
          {
            label: 'Ja, mijn e-mailadres is correct',
            variant: 'primary',
            small: true,
            onClick: () => void handleSubmit(onSubmit)(),
          },
        ]}
        onClose={() => setShowWarning(false)}
        small
      />
      <ContactFormWrapper onSubmit={handleSubmit(onSubmit)}>
        {formMessage && <FormMessage {...formMessage} />}
        <h3>{title}</h3>
        {label && <LabelWrapper>{label}</LabelWrapper>}
        <Row>
          <Column>
            <InputField
              type="text"
              label="Voornaam"
              error={errors?.['first-name']?.message}
              {...register('first-name')}
            />
          </Column>
          <Column>
            <InputField type="text" label="Achternaam" error={errors?.surname?.message} {...register('surname')} />
          </Column>
        </Row>
        <Row>
          <Column>
            <InputField type="text" label="E-mailadres" error={errors?.email?.message} {...register('email')} />
            {suggestion && suggestion !== userEmail && (
              <Suggestion onClick={() => setValue('email', suggestion)}>
                Bedoelt u: <span>{suggestion}</span>?
              </Suggestion>
            )}
          </Column>
          <Column>
            <InputField
              type="text"
              label="Telefoonnummer"
              error={errors?.['phone-number']?.message}
              {...register('phone-number')}
            />
          </Column>
        </Row>
        <Row>
          <Column>
            <SelectField
              options={contactSubjectKeys}
              label="Onderwerp"
              error={errors?.['subject']?.message}
              {...register('subject')}
            />
          </Column>
          <Column>
            {address ? (
              <>
                <InputField type="text" label="Adres" disabled={true} value={address} />
                <div
                  onClick={() => {
                    setAddress(null)
                    setDossier(null)
                  }}
                  className="reset"
                >
                  Wijzig adres
                </div>
              </>
            ) : (
              <AddressSearch
                value={address || ''}
                onValue={data => {
                  setAddress(data.address)
                  setDossier(data.dossier)
                }}
              />
            )}
          </Column>
        </Row>
        <Row>
          <TextArea rows={5} cols={60} label="Uw bericht" error={errors?.text?.message} {...register('text')} />
        </Row>
        <Button type="submit" isLoading={isLoading}>
          Verzenden
        </Button>
        <Modal
          open={isSuccessModalOpen}
          onClose={() => {
            setIsSuccessModalOpen(false)
            onSubmitted()
          }}
          title="Bedankt voor uw bericht!"
          content={`<p>We nemen zo spoedig mogelijk contact met u op.</p>`}
        />
      </ContactFormWrapper>
    </>
  )
}

export default ContactForm
